import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

export default function TechniquePage() {
  const { slug } = useParams();
  const [tech, setTech] = useState(null);
  const [locked, setLocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    client.get(`/content/techniques/${slug}`).then(({ data }) => {
      setTech(data.technique);
      setLocked(data.locked);
    }).catch(() => setNotFound(true));
  }, [slug, navigate]);

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-mizizi-cream text-center px-6">
        <div>
          <div className="overline text-mizizi-terracotta">Not found</div>
          <h1 className="font-serif text-4xl mt-4">We couldn't find that technique.</h1>
          <Link to="/read" className="pill pill-outline mt-8 inline-flex">Back to Reader</Link>
        </div>
      </div>
    );
  }
  if (!tech) return <div className="min-h-[70vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;

  return (
    <div className="bg-mizizi-cream min-h-screen" data-testid={`technique-${tech.slug}`}>
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>

      <header className="max-w-3xl mx-auto px-6 md:px-10 pb-10">
        <div className="overline text-mizizi-gold">Technique</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-[0.95] text-mizizi-charcoal">{tech.title}</h1>
        <p className="mt-6 italic text-mizizi-charcoal/70 text-xl">{tech.subtitle}</p>
        <p className="mt-8 font-serif text-2xl text-mizizi-charcoal leading-snug">{tech.hero}</p>
        {tech.story && (
          <p className="mt-6 text-mizizi-charcoal/80 leading-relaxed">{tech.story}</p>
        )}
      </header>

      <Divider />

      {locked ? (
        <section className="max-w-2xl mx-auto px-6 md:px-10 py-20 text-center">
          <div className="overline text-mizizi-terracotta">Buyer chapter</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 text-mizizi-charcoal">The step-by-step is unlocked with the cookbook.</h2>
          <Link to="/shop" data-testid="technique-buy" className="pill pill-primary mt-8 inline-flex">Get the Cookbook</Link>
        </section>
      ) : (
        <>
          <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16">
            <div className="overline text-mizizi-terracotta text-center">The Method</div>
            <ol className="mt-10 space-y-8">
              {tech.steps.map((s) => (
                <li key={s.n} className="grid grid-cols-[56px_1fr] gap-6" data-testid={`step-${s.n}`}>
                  <div className="font-serif text-4xl text-mizizi-gold leading-none">{String(s.n).padStart(2, "0")}</div>
                  <div>
                    <div className="font-serif text-xl md:text-2xl text-mizizi-charcoal leading-tight">{s.title}</div>
                    <p className="mt-2 text-mizizi-charcoal/80 leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {tech.tips?.length > 0 && (
            <section className="bg-mizizi-forest text-mizizi-cream py-16">
              <div className="max-w-3xl mx-auto px-6 md:px-10">
                <div className="overline text-mizizi-gold">Notes</div>
                <ul className="mt-6 space-y-3">
                  {tech.tips.map((t) => (
                    <li key={t} className="flex gap-3 leading-relaxed">
                      <span className="text-mizizi-gold mt-1">✦</span>
                      <span className="text-mizizi-cream/90">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {tech.related_recipes?.length > 0 && (
            <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
              <div className="overline text-mizizi-terracotta text-center">Cook this into</div>
              <div className="mt-8 space-y-3">
                {tech.related_recipes.map((rslug) => (
                  <Link
                    key={rslug}
                    to={`/read/recipe/${rslug}`}
                    data-testid={`related-${rslug}`}
                    className="flex items-center justify-between p-5 border border-mizizi-gold/30 hover:bg-mizizi-forest hover:text-mizizi-cream transition-colors group"
                  >
                    <span className="font-serif text-xl">{rslug.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}</span>
                    <span className="overline text-mizizi-charcoal/50 group-hover:text-mizizi-gold">Read →</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
